import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, 
  Gamepad2, 
  DollarSign, 
  TrendingUp, 
  LogOut,
  Search,
  Ban,
  CheckCircle,
  Loader2,
  Shield,
  ShieldOff,
  Wallet,
  ArrowRightLeft,
  Gift,
  MessageSquare,
  Lock,
  RefreshCw,
  ExternalLink,
  Crown
} from 'lucide-react';
import { ClickableAvatar } from '../../components/UserStatsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [pendingGamepasses, setPendingGamepasses] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [availableDeposits, setAvailableDeposits] = useState([]);
  const [allDeposits, setAllDeposits] = useState([]);
  const [coldWallets, setColdWallets] = useState({ btc: '', ltc: '', usdt: '' });
  const [coldTransfers, setColdTransfers] = useState([]);
  const [transferCounts, setTransferCounts] = useState({ pending: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminRole, setAdminRole] = useState('admin');
  const [hideTestUsers, setHideTestUsers] = useState(true); // Hide test users by default
  const [walletEditing, setWalletEditing] = useState(false);
  const [walletForm, setWalletForm] = useState({ btc: '', ltc: '', usdt: '' });
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();

  const adminToken = localStorage.getItem('adminToken');

  const api = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${adminToken}` }
  });

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    const role = localStorage.getItem('adminRole') || 'admin';
    setAdminRole(role);
    fetchData();
    if (role === 'owner') {
      fetchColdWalletData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, gamesRes, depositsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users?limit=50'),
        api.get('/admin/games?limit=20'),
        api.get('/admin/deposits?limit=50').catch(() => ({ data: { deposits: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setGames(gamesRes.data.games);
      setAllDeposits(depositsRes.data.deposits || []);

      // Fetch P2P gamepass purchases
      try {
        const [gamepassRes] = await Promise.all([
          api.get('/admin/gamepass-purchases').catch(() => ({ data: { purchases: [] } }))
        ]);
        setPendingGamepasses(gamepassRes.data.purchases || []);
      } catch (e) {
        console.log('P2P endpoints not available');
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchColdWalletData = async () => {
    try {
      const [walletsRes, transfersRes] = await Promise.all([
        api.get('/owner/cold-wallets'),
        api.get('/owner/cold-transfers')
      ]);
      setColdWallets(walletsRes.data.wallets);
      setWalletForm(walletsRes.data.wallets);
      setColdTransfers(transfersRes.data.transfers);
      setTransferCounts(transfersRes.data.counts);
    } catch (error) {
      console.log('Cold wallet endpoints not available');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    navigate('/admin');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const token = localStorage.getItem('adminToken');
    if (!token) {
      toast.error('Not logged in');
      navigate('/admin');
      return;
    }
    try {
      const response = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(searchQuery)}`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setSearchResults(data.users || []);
      if (data.users?.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed: ' + error.message);
    }
  };

  const handleBanUser = async (userId, isBanned) => {
    try {
      await api.post(`/admin/users/${userId}/${isBanned ? 'unban' : 'ban'}`);
      toast.success(isBanned ? 'User unbanned' : 'User banned');
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleMakeAdmin = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/make-admin`);
      toast.success('User is now an admin');
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error('Failed to make user admin');
    }
  };

  const handleRemoveAdmin = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/remove-admin`);
      toast.success('Admin privileges removed');
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleUpdateChessRating = async (userId, username) => {
    // First get current rating
    try {
      const res = await api.get(`/admin/users/${userId}/chess-rating`);
      const currentRating = res.data.rating || 1000;
      const newRating = prompt(`Enter new chess rating for ${username} (100-3000):`, currentRating);
      if (newRating === null) return;
      
      const rating = parseInt(newRating);
      if (isNaN(rating) || rating < 100 || rating > 3000) {
        toast.error('Rating must be between 100 and 3000');
        return;
      }
      
      await api.post(`/admin/users/${userId}/chess-rating`, { 
        rating: rating,
        calibrated: true
      });
      toast.success(`Chess rating updated to ${rating}`);
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update chess rating');
    }
  };

  const handleUpdateBalance = async (userId, currentBalance) => {
    const newBalance = prompt('Enter new balance:', currentBalance);
    if (newBalance === null) return;
    
    try {
      await api.put(`/admin/users/${userId}`, { site_balance: parseFloat(newBalance) });
      toast.success('Balance updated');
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const handleAddBalance = async (userId, username) => {
    const amount = prompt(`Enter amount to add to ${username}'s balance (use negative to subtract):`, '1000');
    if (amount === null) return;
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      toast.error('Invalid amount');
      return;
    }
    
    try {
      const response = await api.post(`/admin/users/${userId}/add-balance`, { amount: numAmount });
      toast.success(`${numAmount >= 0 ? 'Added' : 'Subtracted'} R$${Math.abs(numAmount)} - New balance: R$${response.data.new_balance.toFixed(0)}`);
      fetchData();
      if (searchResults.length > 0) handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add balance');
    }
  };

  const handleVerifyGamepass = async (gamepassId) => {
    try {
      await api.post(`/deposit/gamepass/${gamepassId}/verify`);
      toast.success('Gamepass verified and balance credited');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    }
  };

  const handleRejectGamepass = async (gamepassId) => {
    const reason = prompt('Rejection reason:', 'Could not verify purchase');
    if (!reason) return;
    try {
      await api.post(`/deposit/gamepass/${gamepassId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Gamepass rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleMatchP2P = async (withdrawalId, depositId) => {
    try {
      await api.post(`/p2p/match?withdrawal_id=${withdrawalId}&deposit_id=${depositId}`);
      toast.success('P2P matched successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to match');
    }
  };

  const handleCompleteP2P = async (withdrawalId) => {
    try {
      await api.post(`/p2p/complete/${withdrawalId}`);
      toast.success('P2P transfer completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    }
  };

  // Cold wallet management (owner only)
  const handleUpdateColdWallets = async () => {
    try {
      const res = await api.put('/owner/cold-wallets', walletForm);
      setColdWallets(res.data.wallets);
      setWalletEditing(false);
      toast.success(res.data.message || 'Cold wallets updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wallets');
    }
  };

  const handleRegenerateAllAddresses = async () => {
    if (!window.confirm('This will regenerate crypto deposit addresses for ALL users. Continue?')) {
      return;
    }
    
    setRegenerating(true);
    try {
      const res = await api.post('/owner/regenerate-all-addresses');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate addresses');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCompleteTransfer = async (transferId) => {
    const txHash = prompt('Enter transaction hash (optional):');
    try {
      await api.post(`/owner/cold-transfers/${transferId}/complete?tx_hash=${encodeURIComponent(txHash || '')}`);
      toast.success('Transfer marked as completed');
      fetchColdWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete transfer');
    }
  };

  const handleFailTransfer = async (transferId) => {
    const reason = prompt('Reason for failure:');
    if (!reason) return;
    try {
      await api.post(`/owner/cold-transfers/${transferId}/fail?reason=${encodeURIComponent(reason)}`);
      toast.success('Transfer marked as failed');
      fetchColdWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transfer');
    }
  };

  const renderUserRow = (user, showActions = true) => (
    <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
      <td className="p-4">
        <div className="flex items-center gap-3">
          <ClickableAvatar
            userId={user.user_id}
            username={user.username}
            avatarUrl={user.roblox_avatar_url}
            size={32}
            borderColor="border-slate-600"
          />
          <div>
            <span className="text-white font-medium">{user.username}</span>
            {user.role === 'owner' && <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs">Owner</span>}
            {user.role === 'admin' && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs">Admin</span>}
          </div>
        </div>
      </td>
      <td className="p-4 text-slate-400">{user.email}</td>
      <td className="p-4 text-slate-400">{user.roblox_username || '-'}</td>
      <td className="p-4 text-right">
        <button 
          onClick={() => handleUpdateBalance(user.user_id, user.site_balance)}
          className="text-green-400 font-mono hover:underline"
        >
          R$ {user.site_balance?.toLocaleString()}
        </button>
      </td>
      <td className="p-4 text-center">
        {user.is_banned ? (
          <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs">Banned</span>
        ) : user.is_verified ? (
          <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">Verified</span>
        ) : (
          <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs">Pending</span>
        )}
      </td>
      {showActions && (
        <td className="p-4 text-right space-x-2">
          <Button
            onClick={() => handleAddBalance(user.user_id, user.username)}
            variant="ghost"
            size="sm"
            className="text-green-400"
            title="Add Balance"
          >
            <DollarSign size={16} />
          </Button>
          <Button
            onClick={() => handleBanUser(user.user_id, user.is_banned)}
            variant="ghost"
            size="sm"
            className={user.is_banned ? 'text-green-400' : 'text-red-400'}
            title={user.is_banned ? 'Unban' : 'Ban'}
          >
            {user.is_banned ? <CheckCircle size={16} /> : <Ban size={16} />}
          </Button>
          {user.role !== 'owner' && (
            user.is_admin || user.role === 'admin' ? (
              <Button
                onClick={() => handleRemoveAdmin(user.user_id)}
                variant="ghost"
                size="sm"
                className="text-orange-400"
                title="Remove Admin"
              >
                <ShieldOff size={16} />
              </Button>
            ) : (
              <Button
                onClick={() => handleMakeAdmin(user.user_id)}
                variant="ghost"
                size="sm"
                className="text-purple-400"
                title="Make Admin"
              >
                <Shield size={16} />
              </Button>
            )
          )}
          <Button
            onClick={() => handleUpdateChessRating(user.user_id, user.username)}
            variant="ghost"
            size="sm"
            className="text-yellow-400"
            title="Set Chess Rating"
          >
            <Crown size={16} />
          </Button>
        </td>
      )}
    </tr>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-display text-xl font-bold">
              <span className="text-slate-300">RBX</span>
              <span className="text-red-500">CASINO</span>
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              adminRole === 'owner' ? 'bg-yellow-600/20 text-yellow-400' : 'bg-red-600/20 text-red-400'
            }`}>
              {adminRole.toUpperCase()}
            </span>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-slate-400 hover:text-red-400">
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: 'overview', icon: TrendingUp, label: 'Overview' },
            { id: 'users', icon: Users, label: 'Users' },
            { id: 'games', icon: Gamepad2, label: 'Games' },
            { id: 'deposits', icon: Wallet, label: 'Deposits' },
            { id: 'p2p', icon: ArrowRightLeft, label: 'P2P' },
            ...(adminRole === 'owner' ? [{ id: 'wallets', icon: Lock, label: 'Cold Wallets', ownerOnly: true }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? tab.ownerOnly ? 'bg-yellow-600 text-white' : 'bg-red-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.id === 'deposits' && pendingGamepasses.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-yellow-500 text-black text-xs">{pendingGamepasses.length}</span>
              )}
              {tab.id === 'p2p' && pendingWithdrawals.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-yellow-500 text-black text-xs">{pendingWithdrawals.length}</span>
              )}
              {tab.id === 'wallets' && transferCounts.pending > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-yellow-500 text-black text-xs">{transferCounts.pending}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <Users className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                    <p className="text-xs text-slate-500">{stats.verified_users} verified</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-600/20 flex items-center justify-center">
                    <Gamepad2 className="text-purple-400" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Total Games</p>
                    <p className="text-2xl font-bold text-white">{stats.total_games}</p>
                    <p className="text-xs text-slate-500">{stats.win_rate?.toFixed(1)}% win rate</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-yellow-600/20 flex items-center justify-center">
                    <DollarSign className="text-yellow-400" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Total Wagered</p>
                    <p className="text-2xl font-bold text-white">R$ {stats.total_wagered?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-600/20 flex items-center justify-center">
                    <TrendingUp className="text-green-400" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">House Profit</p>
                    <p className={`text-2xl font-bold ${stats.house_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      R$ {stats.house_profit?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4">Quick User Search</h3>
              <div className="flex gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by username..."
                    className="input-default pl-10"
                  />
                </div>
                <Button onClick={handleSearch} className="btn-primary">Search</Button>
              </div>
              
              {searchResults.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-3 text-slate-400 font-medium">User</th>
                        <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                        <th className="text-left p-3 text-slate-400 font-medium">Roblox</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Balance</th>
                        <th className="text-center p-3 text-slate-400 font-medium">Status</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((user) => renderUserRow(user))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Hide Test Users Toggle */}
            <div className="glass rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Hide Test Users</p>
                <p className="text-slate-500 text-sm">Filter out users with "test" in their username</p>
              </div>
              <button
                onClick={() => setHideTestUsers(!hideTestUsers)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  hideTestUsers ? 'bg-green-500' : 'bg-slate-700'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  hideTestUsers ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>

            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-slate-400 font-medium">User</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Roblox</th>
                    <th className="text-right p-4 text-slate-400 font-medium">Balance</th>
                    <th className="text-center p-4 text-slate-400 font-medium">Status</th>
                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users
                    .filter(user => !hideTestUsers || !user.username?.toLowerCase().includes('test'))
                    .map((user) => renderUserRow(user))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Games Tab */}
        {activeTab === 'games' && (
          <div className="glass rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-slate-400 font-medium">Game</th>
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Bet</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Payout</th>
                  <th className="text-center p-4 text-slate-400 font-medium">Result</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {games.map((game, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 text-white capitalize">{game.game_type}</td>
                    <td className="p-4 text-slate-400">{game.username}</td>
                    <td className="p-4 text-right text-white font-mono">R$ {game.bet_amount?.toFixed(0)}</td>
                    <td className="p-4 text-right font-mono">
                      <span className={game.won ? 'text-green-400' : 'text-red-400'}>
                        R$ {game.payout?.toFixed(0) || 0}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {game.won ? (
                        <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">Won</span>
                      ) : (
                        <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs">Lost</span>
                      )}
                    </td>
                    <td className="p-4 text-right text-slate-500 text-sm">
                      {new Date(game.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <div className="space-y-6">
            {/* All Deposits History */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Wallet className="text-green-400" />
                Deposit History ({allDeposits.length})
              </h3>
              {allDeposits.length === 0 ? (
                <p className="text-slate-400">No deposits yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                        <th className="pb-3">User</th>
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Amount</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDeposits.map((d, i) => (
                        <tr key={d.deposit_id || i} className="border-b border-slate-800">
                          <td className="py-3 text-white">{d.username}</td>
                          <td className="py-3">
                            {d.type === 'crypto' ? (
                              <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-xs">
                                {d.crypto_type}
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">
                                Robux
                              </span>
                            )}
                          </td>
                          <td className="py-3 text-green-400 font-mono">
                            R$ {d.amount?.toLocaleString()}
                            {d.crypto_amount && (
                              <span className="text-slate-500 text-xs ml-2">
                                ({d.crypto_amount} {d.crypto_type})
                              </span>
                            )}
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              d.status === 'completed' || d.status === 'confirmed' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {d.status}
                            </span>
                          </td>
                          <td className="py-3 text-slate-500 text-sm">
                            {d.created_at ? new Date(d.created_at).toLocaleString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pending Gamepass Verifications */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Gift className="text-yellow-400" />
                Pending Gamepass Verifications
              </h3>
              {pendingGamepasses.length === 0 ? (
                <p className="text-slate-400">No pending gamepasses</p>
              ) : (
                <div className="space-y-4">
                  {pendingGamepasses.map((gp) => (
                    <div key={gp.gamepass_id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                      <div>
                        <p className="text-white font-medium">{gp.username}</p>
                        <p className="text-slate-400 text-sm">Gamepass ID: {gp.roblox_gamepass_id}</p>
                        <p className="text-slate-400 text-sm">Expected: R$ {gp.expected_price?.toLocaleString()}</p>
                        <p className="text-slate-500 text-xs">{new Date(gp.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleVerifyGamepass(gp.gamepass_id)} className="bg-green-600 hover:bg-green-500">
                          <CheckCircle size={16} className="mr-1" /> Verify
                        </Button>
                        <Button onClick={() => handleRejectGamepass(gp.gamepass_id)} variant="ghost" className="text-red-400">
                          <Ban size={16} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* P2P Tab */}
        {activeTab === 'p2p' && (
          <div className="space-y-6">
            {/* Pending Withdrawals */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <ArrowRightLeft className="text-red-400" />
                Pending Withdrawals
              </h3>
              {pendingWithdrawals.length === 0 ? (
                <p className="text-slate-400">No pending withdrawals</p>
              ) : (
                <div className="space-y-4">
                  {pendingWithdrawals.map((w) => (
                    <div key={w.withdrawal_id} className="p-4 rounded-lg bg-slate-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{w.username}</p>
                          <p className="text-slate-400">To: {w.roblox_username}</p>
                          <p className="text-green-400 font-mono">R$ {w.amount?.toLocaleString()}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {w.status === 'pending' && availableDeposits.length > 0 && (
                            <select 
                              className="bg-slate-700 text-white rounded px-3 py-2 text-sm"
                              onChange={(e) => e.target.value && handleMatchP2P(w.withdrawal_id, e.target.value)}
                              defaultValue=""
                            >
                              <option value="">Match with deposit...</option>
                              {availableDeposits.map((d) => (
                                <option key={d.deposit_id} value={d.deposit_id}>
                                  {d.username} - R$ {d.amount}
                                </option>
                              ))}
                            </select>
                          )}
                          {w.status === 'matched' && (
                            <Button onClick={() => handleCompleteP2P(w.withdrawal_id)} className="bg-green-600 hover:bg-green-500">
                              Complete Transfer
                            </Button>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        w.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        w.status === 'matched' ? 'bg-blue-500/20 text-blue-400' :
                        w.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {w.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Deposits */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Wallet className="text-green-400" />
                Available P2P Deposits
              </h3>
              {availableDeposits.length === 0 ? (
                <p className="text-slate-400">No available deposits</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableDeposits.map((d) => (
                    <div key={d.deposit_id} className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-white font-medium">{d.username}</p>
                      <p className="text-slate-400 text-sm">{d.roblox_username}</p>
                      <p className="text-green-400 font-mono text-lg">R$ {d.amount?.toLocaleString()}</p>
                      <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">Available</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cold Wallets Tab (Owner Only) */}
        {activeTab === 'wallets' && adminRole === 'owner' && (
          <div className="space-y-6">
            {/* Cold Wallet Addresses */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Lock className="text-yellow-400" />
                  Cold Wallet Addresses
                </h3>
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchColdWalletData} 
                    variant="ghost" 
                    className="text-slate-400"
                  >
                    <RefreshCw size={16} />
                  </Button>
                  {!walletEditing ? (
                    <Button onClick={() => setWalletEditing(true)} className="bg-yellow-600 hover:bg-yellow-500">
                      Edit Wallets
                    </Button>
                  ) : (
                    <>
                      <Button onClick={() => setWalletEditing(false)} variant="ghost" className="text-slate-400">
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateColdWallets} className="bg-green-600 hover:bg-green-500">
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <p className="text-slate-400 text-sm mb-4">
                All crypto deposits forward to these wallet addresses. When you change wallets, all user deposit addresses will be regenerated.
              </p>

              {/* Regenerate All Addresses Button */}
              <div className="mb-4">
                <Button 
                  onClick={handleRegenerateAllAddresses}
                  className="bg-blue-600 hover:bg-blue-500"
                  disabled={regenerating}
                >
                  {regenerating ? (
                    <><Loader2 className="animate-spin mr-2" size={16} /> Regenerating...</>
                  ) : (
                    'Regenerate All User Addresses'
                  )}
                </Button>
                <p className="text-slate-500 text-xs mt-1">
                  Use this if addresses need to be refreshed with new callbacks
                </p>
              </div>

              <div className="space-y-4">
                {/* BTC */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-orange-500/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm">₿</div>
                    <span className="text-white font-medium">Bitcoin (BTC)</span>
                  </div>
                  {walletEditing ? (
                    <Input
                      value={walletForm.btc}
                      onChange={(e) => setWalletForm({ ...walletForm, btc: e.target.value })}
                      placeholder="Enter BTC address"
                      className="input-default font-mono text-sm"
                    />
                  ) : (
                    <p className="text-slate-300 font-mono text-sm break-all">{coldWallets.btc || 'Not configured'}</p>
                  )}
                </div>

                {/* LTC */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-500/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-slate-500/20 flex items-center justify-center text-slate-300 font-bold text-sm">Ł</div>
                    <span className="text-white font-medium">Litecoin (LTC)</span>
                  </div>
                  {walletEditing ? (
                    <Input
                      value={walletForm.ltc}
                      onChange={(e) => setWalletForm({ ...walletForm, ltc: e.target.value })}
                      placeholder="Enter LTC address"
                      className="input-default font-mono text-sm"
                    />
                  ) : (
                    <p className="text-slate-300 font-mono text-sm break-all">{coldWallets.ltc || 'Not configured'}</p>
                  )}
                </div>

                {/* USDT */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-green-500/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm">₮</div>
                    <span className="text-white font-medium">USDT (Tron TRC-20)</span>
                  </div>
                  {walletEditing ? (
                    <Input
                      value={walletForm.usdt}
                      onChange={(e) => setWalletForm({ ...walletForm, usdt: e.target.value })}
                      placeholder="Enter USDT TRC-20 address"
                      className="input-default font-mono text-sm"
                    />
                  ) : (
                    <p className="text-slate-300 font-mono text-sm break-all">{coldWallets.usdt || 'Not configured'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Pending Transfers */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <ArrowRightLeft className="text-yellow-400" />
                Cold Wallet Transfers
                <span className="ml-2 text-sm font-normal text-slate-400">
                  ({transferCounts.pending} pending, {transferCounts.completed} completed, {transferCounts.failed} failed)
                </span>
              </h3>

              {coldTransfers.length === 0 ? (
                <p className="text-slate-400">No transfers yet. Transfers are automatically created when users wager 90% of their crypto deposits.</p>
              ) : (
                <div className="space-y-4">
                  {coldTransfers.map((transfer) => (
                    <div key={transfer.transfer_id} className="p-4 rounded-lg bg-slate-800/50 border border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                            transfer.crypto_type === 'btc' ? 'bg-orange-500/20 text-orange-400' :
                            transfer.crypto_type === 'ltc' ? 'bg-slate-500/20 text-slate-300' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {transfer.crypto_type === 'btc' ? '₿' : transfer.crypto_type === 'ltc' ? 'Ł' : '₮'}
                          </div>
                          <div>
                            <p className="text-white font-medium">
                              {transfer.crypto_amount} {transfer.crypto_type.toUpperCase()}
                            </p>
                            <p className="text-slate-500 text-xs">
                              {new Date(transfer.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          transfer.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          transfer.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {transfer.status.toUpperCase()}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">From (Hot):</span>
                          <span className="text-slate-300 font-mono text-xs break-all">{transfer.hot_wallet_address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">To (Cold):</span>
                          <span className="text-green-400 font-mono text-xs break-all">{transfer.cold_wallet_address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Reason:</span>
                          <span className="text-slate-400">{transfer.reason}</span>
                        </div>
                        {transfer.tx_hash && (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">TX Hash:</span>
                            <span className="text-blue-400 font-mono text-xs">{transfer.tx_hash}</span>
                          </div>
                        )}
                      </div>

                      {transfer.status === 'pending' && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                          <Button 
                            onClick={() => handleCompleteTransfer(transfer.transfer_id)} 
                            className="bg-green-600 hover:bg-green-500 flex-1"
                          >
                            <CheckCircle size={16} className="mr-2" />
                            Mark Completed
                          </Button>
                          <Button 
                            onClick={() => handleFailTransfer(transfer.transfer_id)} 
                            variant="ghost" 
                            className="text-red-400"
                          >
                            <Ban size={16} className="mr-2" />
                            Mark Failed
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
